import React, { useContext, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useWalletContext } from 'hooks/context'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface WalletButtonsCryptoProps {
  disabled?: boolean
}

export const WalletButtonsCrypto = ({ disabled }: WalletButtonsCryptoProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext()
  const { enableUsLaunchRestrictions, enableCryptoFiatSend } = useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const wallet = useWalletContext()
  const isSelectedWalletActive = useIsActiveWallet(wallet)
  const permissions = useMappedRoles()
  const canEdit = permissions?.wallets?.canEdit
  const canTrade = permissions?.wallets?.canTrade
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const canEditOrTrade = canEdit || canTrade
  const hasBalance = wallet?.balance > 0

  const customerModel = useAtomValue(customerModelAtom)

  const handleOpenSendSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_SEND, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
    })
    setWalletAction({
      actionId: ActionIds?.SEND,
      header: t('wallets.send.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenConvertSheet = () => {
    track.Amp.track(AmplitudeEvent.WALLET_DETAILS_CONVERT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
      currency: wallet?.currency?.code?.toLowerCase(),
      walletId: wallet?.id,
      isFiat: false,
    })
    setWalletAction({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = () => {
    setWalletAction({
      actionId: ActionIds.MOVE,
      header: t('customers.moveFunds'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        sourceWalletType: WalletTransferType.MAIN,
        sourceWallet: wallet,
        customerModel,
      },
    })
    setIsSheetOpen(true)
  }

  const canSend = canEditOrTrade && canCreatePayOut && isSelectedWalletActive && hasBalance

  return (
    isSelectedWalletActive && (
      <Box flex padding={8} className="gap-3 sm:pt-0">
        {!enableUsLaunchRestrictions && (
          <Button
            testId="buttonConvert"
            disabled={disabled}
            onClick={handleOpenConvertSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('convert')}
          </Button>
        )}

        {enableCryptoFiatSend ? (
          <>
            {canSend && (
              <Button
                testId="send-crypto"
                onClick={handleOpenSendSheet}
                disabled={viewOnly}
                iconElement={<Icon name="ArrowUpRight" />}
              >
                {t('transactionAction.send')}
              </Button>
            )}
          </>
        ) : (
          <>
            {canSend && (
              <Button
                testId="send-crypto"
                onClick={handleOpenSendSheet}
                disabled={viewOnly}
                iconElement={<Icon name="ArrowUpRight" />}
              >
                {t('payments.sendCrypto')}
              </Button>
            )}
          </>
        )}

        {wallet?.supportsInternalTransfers && (
          <Button
            testId="buttonMove"
            disabled={disabled}
            onClick={handleOpenMoveSheet}
            iconElement={<Icon name="ArrowsLeftRight" />}
          >
            {t('customers.move')}
          </Button>
        )}
        <WalletSheet isSheetOpen={isSheetOpen} />
      </Box>
    )
  )
}
